<template>
  <div class="register-page min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-neutral-50">
    <div class="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-sm border border-neutral-100">
      <div class="text-center">
        <h2 class="mt-6 text-3xl font-extrabold text-neutral-900">
          创建新账户
        </h2>
        <p class="mt-2 text-sm text-neutral-600">
          加入我们，探索更多AI工具
        </p>
      </div>

      <!-- 错误/成功提示 -->
      <div v-if="message.content" :class="['p-4 rounded-lg text-sm mb-4', message.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600']">
        <i :class="['fa mr-2', message.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle']"></i>
        {{ message.content }}
      </div>

      <form class="mt-8 space-y-6" @submit.prevent="handleRegister">
        <div class="rounded-md shadow-sm space-y-4">
          <div>
            <label for="username" class="block text-sm font-medium text-neutral-700 mb-1">用户名</label>
            <input 
              id="username" 
              name="username" 
              type="text" 
              required 
              v-model="form.username"
              class="appearance-none relative block w-full px-3 py-3 border border-neutral-300 placeholder-neutral-500 text-neutral-900 rounded-lg focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm" 
              placeholder="请输入用户名"
            >
          </div>
          
          <div>
            <label for="phone" class="block text-sm font-medium text-neutral-700 mb-1">手机号</label>
            <input 
              id="phone" 
              name="phone" 
              type="tel" 
              pattern="[0-9]{11}"
              required 
              v-model="form.phone"
              class="appearance-none relative block w-full px-3 py-3 border border-neutral-300 placeholder-neutral-500 text-neutral-900 rounded-lg focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm" 
              placeholder="请输入11位手机号"
            >
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-neutral-700 mb-1">密码</label>
            <div class="relative">
              <input 
                id="password" 
                name="password" 
                :type="showPassword ? 'text' : 'password'" 
                required 
                v-model="form.password"
                @input="checkPasswordStrength"
                class="appearance-none relative block w-full pl-3 pr-11 py-3 border border-neutral-300 placeholder-neutral-500 text-neutral-900 rounded-lg focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="6-15位，需包含数字/字母/特殊字符至少2种"
              >
              <button
                type="button"
                class="absolute inset-y-0 right-0 flex items-center px-3 text-neutral-400 hover:text-neutral-600 transition-colors"
                :title="showPassword ? '隐藏密码' : '显示密码'"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
              </button>
            </div>
            <!-- 密码强度指示器 -->
            <div v-if="form.password" class="mt-2">
              <div class="flex h-1.5 w-full bg-neutral-200 rounded-full overflow-hidden">
                <div 
                  :class="['h-full transition-all duration-300', passwordStrengthColor]" 
                  :style="{ width: passwordStrengthWidth }"
                ></div>
              </div>
              <p class="text-xs mt-1 text-neutral-500 text-right">{{ passwordStrengthText }}</p>
            </div>
          </div>

          <div>
            <label for="confirm-password" class="block text-sm font-medium text-neutral-700 mb-1">确认密码</label>
            <div class="relative">
              <input 
                id="confirm-password" 
                name="confirm-password" 
                :type="showConfirmPassword ? 'text' : 'password'" 
                required 
                v-model="form.confirmPassword"
                class="appearance-none relative block w-full pl-3 pr-11 py-3 border border-neutral-300 placeholder-neutral-500 text-neutral-900 rounded-lg focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm" 
                placeholder="请再次输入密码"
              >
              <button
                type="button"
                class="absolute inset-y-0 right-0 flex items-center px-3 text-neutral-400 hover:text-neutral-600 transition-colors"
                :title="showConfirmPassword ? '隐藏密码' : '显示密码'"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <i :class="showConfirmPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
              </button>
            </div>
          </div>

          <div>
            <label for="region" class="block text-sm font-medium text-neutral-700 mb-1">所在地区（省 / 市 / 区县）<span class="text-red-500">*</span></label>
            <el-cascader
              id="region"
              v-model="form.region"
              :options="regionOptions"
              :props="{ label: 'name', value: 'name', children: 'children' }"
              placeholder="请选择 省 / 市 / 区县"
              clearable
              class="w-full region-cascader"
            />
          </div>

          <div>
            <label for="unit" class="block text-sm font-medium text-neutral-700 mb-1">单位名称（学校 / 机构）<span class="text-red-500">*</span></label>
            <input 
              id="unit" 
              name="unit" 
              type="text" 
              required 
              v-model="form.unit"
              class="appearance-none relative block w-full px-3 py-3 border border-neutral-300 placeholder-neutral-500 text-neutral-900 rounded-lg focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm" 
              placeholder="请输入学校或单位名称"
            >
          </div>

          <div>
            <label for="stage" class="block text-sm font-medium text-neutral-700 mb-1">学段<span class="text-red-500">*</span></label>
            <select
              id="stage"
              v-model="form.stage"
              required
              class="appearance-none relative block w-full px-3 py-3 border border-neutral-300 text-neutral-900 rounded-lg focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm bg-white"
            >
              <option value="" disabled>请选择学段</option>
              <option v-for="s in stageOptions" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>

          <div>
            <label for="subject" class="block text-sm font-medium text-neutral-700 mb-1">学科<span class="text-red-500">*</span></label>
            <select
              id="subject"
              v-model="form.subject"
              required
              class="appearance-none relative block w-full px-3 py-3 border border-neutral-300 text-neutral-900 rounded-lg focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm bg-white"
            >
              <option value="" disabled>请选择学科</option>
              <option v-for="s in subjectList" :key="s.id" :value="s.title">{{ s.title }}</option>
            </select>
          </div>
        </div>

        <div class="flex items-center">
          <input 
            id="agree-terms" 
            name="agree-terms" 
            type="checkbox" 
            required
            v-model="form.agreeTerms"
            class="h-4 w-4 text-primary focus:ring-primary border-neutral-300 rounded cursor-pointer"
          >
          <label for="agree-terms" class="ml-2 block text-sm text-neutral-900 cursor-pointer">
            我已阅读并同意
            <a href="#" class="font-medium text-primary hover:text-primary-dark">服务条款</a>
            和
            <a href="#" class="font-medium text-primary hover:text-primary-dark">隐私政策</a>
          </label>
        </div>

        <div>
          <button 
            type="submit" 
            :disabled="isLoading"
            class="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-70"
          >
            <span class="absolute left-0 inset-y-0 flex items-center pl-3">
              <i class="fa fa-user-plus group-hover:text-primary-light" aria-hidden="true"></i>
            </span>
            {{ isLoading ? '注册中...' : '注册' }}
          </button>
        </div>
      </form>
      
      <div class="text-center mt-4">
        <p class="text-sm text-neutral-600">
          已有账户? 
          <router-link to="/login" class="font-medium text-primary hover:text-primary-dark">
            立即登录
          </router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import regionData from 'china-division/dist/pca-code.json'

export default {
  name: 'RegisterPage',
  data() {
    return {
      form: {
        username: '',
        phone: '',
        password: '',
        confirmPassword: '',
        region: [],
        unit: '',
        stage: '',
        subject: '',
        agreeTerms: false
      },
      regionOptions: regionData,
      stageOptions: ['幼儿园', '小学', '初中', '普通高中', '职业高中', '其他'],
      subjectList: [],
      message: {
        type: '',
        content: ''
      },
      isLoading: false,
      passwordStrength: 0,
      // 密码可见性切换（点小眼睛）
      showPassword: false,
      showConfirmPassword: false
    }
  },
  created() {
    // 学科列表（与管理端「学科知识体系」共用学科数据）
    this.$axios({ url: 'subinfo/list' }).then((res) => {
      if (res.code === 200) {
        this.subjectList = res.data || [];
      }
    }).catch(() => {});
  },
  computed: {
    passwordStrengthWidth() {
      return (this.passwordStrength / 4) * 100 + '%';
    },
    passwordStrengthColor() {
      if (this.passwordStrength <= 1) return 'bg-red-500';
      if (this.passwordStrength === 2) return 'bg-yellow-500';
      if (this.passwordStrength === 3) return 'bg-blue-500';
      return 'bg-green-500';
    },
    passwordStrengthText() {
      if (this.passwordStrength === 0) return '';
      if (this.passwordStrength <= 1) return '弱';
      if (this.passwordStrength === 2) return '中';
      if (this.passwordStrength === 3) return '强';
      return '极强';
    }
  },
  methods: {
    checkPasswordStrength() {
      let strength = 0;
      const pwd = this.form.password;
      if (pwd.length >= 6) strength++;
      if (pwd.match(/[a-z]/) && pwd.match(/[A-Z]/)) strength++;
      if (pwd.match(/\d/)) strength++;
      if (pwd.match(/[^a-zA-Z\d]/)) strength++;
      this.passwordStrength = strength;
    },
    /**
     * 密码规则：6-15 个字符，需包含数字、字母、特殊字符中的至少 2 种
     * 返回 null 表示合法，否则返回错误提示
     */
    validatePassword(pwd) {
      if (!pwd) return '请输入密码';
      if (pwd.length < 6 || pwd.length > 15) return '密码长度需为 6-15 个字符';
      if (/\s/.test(pwd)) return '密码不能包含空格';
      const kinds = [/a-zA-Z/, /\d/, /[^a-zA-Z\d]/].filter((re) => re.test(pwd)).length;
      if (kinds < 2) return '密码需包含数字、字母、特殊字符中的至少 2 种';
      return null;
    },
    handleRegister() {
      this.message = { type: '', content: '' };
      
      if (this.form.password !== this.form.confirmPassword) {
        this.message = { type: 'error', content: '两次输入的密码不一致' };
        return;
      }
      
      const pwdError = this.validatePassword(this.form.password);
      if (pwdError) {
        this.message = { type: 'error', content: pwdError };
        return;
      }
      if (!this.form.agreeTerms) {
        this.message = { type: 'error', content: '请先同意服务条款和隐私政策' };
        return;
      }
      
      if (!this.form.region || this.form.region.length < 3) {
        this.message = { type: 'error', content: '请选择所在地区（省 / 市 / 区县）' };
        return;
      }
      
      if (!this.form.unit || !this.form.unit.trim()) {
        this.message = { type: 'error', content: '请填写单位名称' };
        return;
      }
      
      if (!this.form.stage) {
        this.message = { type: 'error', content: '请选择学段' };
        return;
      }
      
      if (!this.form.subject) {
        this.message = { type: 'error', content: '请选择学科' };
        return;
      }
      
      this.isLoading = true;
      
      // 调用后端注册接口
      this.$axios({
        url: 'user/register',
        method: 'PUT',
        data: {
          name: this.form.username,
          phone: this.form.phone,
          loginId: this.form.username,
          password: this.form.password,
          province: this.form.region[0],
          city: this.form.region[1],
          district: this.form.region[2],
          schName: this.form.unit.trim(),
          stage: this.form.stage,
          subject: this.form.subject
        }
      }).then((res) => {
        this.isLoading = false;
        if (res.code == 200) {
          this.message = { type: 'success', content: '注册成功！正在跳转登录...' };
          // 跳转登录页
          setTimeout(() => {
            this.$router.push('/login');
          }, 1500);
        } else {
          this.message = { type: 'error', content: res.msg || '注册失败' };
        }
      }).catch((err) => {
        this.isLoading = false;
        this.message = { type: 'error', content: '注册失败，请稍后重试' };
        console.error(err);
      });
    }
  }
}
</script>

<style scoped>
/* 级联选择器与表单输入框高度对齐 */
.region-cascader :deep(.el-input__wrapper) {
  padding: 4px 12px;
  border-radius: 8px;
}
.region-cascader :deep(.el-input__inner) {
  height: 40px;
  line-height: 40px;
}
</style>
